import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import type { PersonalOfferToggleModel } from 'autogen/swagger/all';
import { useDisableMutation, useEnableMutation } from 'autogen/swagger/all/hooks';
import {
    ComponentsLabelNewBg as KicksLabelNewBg,
    ComponentsLabelNewText as KicksLabelNewText
} from 'autogen/theming/themes/kicks/default';
import {
    ComponentsLabelNewBg as MatasLabelNewBg,
    ComponentsLabelNewText as MatasLabelNewText
} from 'autogen/theming/themes/matas/default';
import {
    PersonalOfferTag,
    TermsButtonText,
    ToggleActivatedMessage,
    ToggleActiveTag,
    ToggleActiveText,
    ToggleNotActiveText,
    ToggleOffErrorMessage,
    ToggleOnErrorMessage
} from 'autogen/translation-keys/trans-website-personaloffer-toggle';
import { Close } from 'autogen/translation-keys/trans-website-shared';
import { GeneralExceptionError } from 'autogen/translation-keys/trans-website-shared-error';
import { Button } from 'components/Button';
import Card from 'components/generic/card';
import ControlledToggleButton from 'components/generic/Toggle/components/ControlledToggleButton';
import Tag from 'components/generic/tag';
import { Typography } from 'components/generic/typography';
import QuickView, { useQuickView } from 'components/quickView';
import ActionFooter from 'components/quickView/components/ActionFooter';
import { useGetProductListPartial } from 'components/shared/productList/shared/hooks/useGetProductListPartialQuery';
import toast from 'components/Toast';
import format from 'helpers/translations';
import useSegmentation from 'hooks/user/useSegmentation';
import { customScrollbar } from 'modules/helpers/mixins';
import useGA4Tracking from 'modules/tracking/GA4';
import ThemeProvider from 'theming/ThemeProvider';
import { breakpoint } from 'variables';

export type PersonalOfferToggleProps = {
    model: PersonalOfferToggleModel;
    disclaimer?: string;
};

const StyledCard = styled(Card)`
    grid-area: offerToggle;
    color: ${({ theme }) => theme.ColorTextPrimary};
    background: ${({ theme }) => theme.ColorBackgroundPrimary};
`;

const StyledCardContent = styled(Card.Content)<{ $hasDisclaimer: boolean }>`
    display: grid;
    grid-template-columns: 1fr max-content;
    gap: ${({ theme }) => theme.Spacing8};
    padding: ${({ theme }) => theme.Spacing24};

    ${({ $hasDisclaimer }) =>
        $hasDisclaimer
            ? css`
                grid-template-areas:
                    'tags toggle'
                    'heading toggle'
                    'disclaimer toggle'
                ;`
            : css`
                grid-template-areas:
                    'tags toggle'
                    'heading toggle';
                `}
    
    ${breakpoint.up('768px')} {
        column-gap: ${({ theme }) => theme.Spacing16};
        padding: ${({ theme }) => `${theme.Spacing24} ${theme.Spacing32}`};
    }
`;

const StyledHeadline = styled(Typography.Headline)`
    grid-area: heading;
`;

const ToggleContainer = styled.div`
    grid-area: toggle;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: ${({ theme }) => theme.Spacing4};
`;

const StyledToggle = styled(ControlledToggleButton)`
    justify-self: end;
`;

const Tags = styled.div`
    display: flex;
    gap: ${({ theme }) => theme.Spacing8};
    grid-area: tags;
`;

const StyledTag = styled(Tag)`
    width: fit-content;
`;

const StyledButton = styled(Button)`
    grid-area: disclaimer;
    width: fit-content;
`;

const StyledQuickView = styled(QuickView)`
    // && for specificity
    && {
        ${breakpoint.up('768px')} {
            min-width: 480px;
            width: 480px;
        }
    }
`;

export const ScrollContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 0 1 100%;
    gap: ${({ theme }) => theme.Spacing16};
    padding: ${({ theme }) => `0 ${theme.Spacing24} ${theme.Spacing24}`};

    ${customScrollbar}
`;

function PersonalOfferToggle(props: PersonalOfferToggleProps) {
    const { model, disclaimer } = props;
    const { discountId, isEnabled, offerName } = model;
    const [isToggleActive, setIsToggleActive] = useState(isEnabled);
    const GA4Tracking = useGA4Tracking();
    const { toggle } = useQuickView('PersonalOfferTermsQuickView');
    const { t } = useTranslation();
    const { tenant } = useSegmentation();
    const isKicks = tenant === 'Kicks';

    const { mutateAsync: enableOffer } = useEnableMutation();
    const { mutateAsync: disableOffer } = useDisableMutation();
    const { refetch: refetchProductList } = useGetProductListPartial();

    function getActiveTagColor() {
        return {
            backgroundColor: isKicks ? KicksLabelNewBg : MatasLabelNewBg,
            textColor: isKicks ? KicksLabelNewText : MatasLabelNewText
        };
    }

    async function handleToggleOnChange() {
        try {
            setIsToggleActive(!isToggleActive);

            if (!isToggleActive) {
                const { success } = await enableOffer({ params: { discountId } });

                if (success) {
                    toast.success(t(ToggleActivatedMessage));
                } else {
                    setIsToggleActive(false);
                    toast.error(t(ToggleOnErrorMessage));
                }
            } else {
                const { success } = await disableOffer({ params: { discountId } });

                if (!success) {
                    toast.error(t(ToggleOffErrorMessage));
                }
            }

            refetchProductList();

            GA4Tracking({
                eventName: 'toggle_personal_offer',
                category: 'ecommerce',
                eventParams: [{ param_name: 'toggle', param_value: !isToggleActive }]
            });
        } catch (error) {
            setIsToggleActive(false);
            toast.error(t(GeneralExceptionError));
            console.error(error);
        }
    }

    return (
        <ThemeProvider theme="club1">
            <StyledCard>
                <StyledCardContent $hasDisclaimer={!!disclaimer}>
                    <Tags>
                        <StyledTag variant="neutral">{t(PersonalOfferTag)}</StyledTag>
                        {isToggleActive && <StyledTag variant={getActiveTagColor()}>{t(ToggleActiveTag)}</StyledTag>}
                    </Tags>
                    <StyledHeadline size="L">{offerName}</StyledHeadline>
                    {disclaimer && (
                        <StyledButton variant="negative" size="sm" onClick={() => toggle(true)}>
                            {t(TermsButtonText)}
                        </StyledButton>
                    )}
                    <ToggleContainer>
                        <StyledToggle active={isToggleActive} onChange={handleToggleOnChange} />
                        <Typography.Body size="S" strong>
                            {t(isToggleActive ? ToggleActiveText : ToggleNotActiveText)}
                        </Typography.Body>
                    </ToggleContainer>
                </StyledCardContent>
            </StyledCard>
            {disclaimer && (
                <StyledQuickView
                    quickViewId={'PersonalOfferTermsQuickView'}
                    position="right"
                    footerRenderer={() => (
                        <ActionFooter>
                            <Button variant="primary" onClick={() => toggle(false)}>
                                {t(Close)}
                            </Button>
                        </ActionFooter>
                    )}
                >
                    <ScrollContainer>
                        <Typography.Body size="L">{format(disclaimer)}</Typography.Body>
                    </ScrollContainer>
                </StyledQuickView>
            )}
        </ThemeProvider>
    );
}

export default PersonalOfferToggle;
